/**
 * Local usage counter.
 *
 * The audit's finding was that this product has **no social proof at all** — no
 * stars, no counts, no quotes — while asking people to trust it with
 * career-critical work. The obvious fix is a server-side counter, and that is
 * precisely the fix this product cannot take: a runtime call to any endpoint
 * would falsify the claim the privacy page invites users to verify in DevTools.
 *
 * So this counts what happened *in this browser*, and says exactly that.
 *
 * "You have analysed 14 resumes here" is a genuinely weaker boast than "2 million
 * users" — and it is *true*, checkable, and costs nothing to make. Inventing the
 * larger number is the thing this codebase has consistently refused to do, and
 * the honest small number is more persuasive to the sceptical technical reader
 * who is the exact person most likely to be converted by it.
 *
 * Never leaves the device. Never transmitted. No identifier of any kind is
 * generated — this is a single integer and a date string.
 */

const KEY = 'ats-resume:usage'

export interface UsageStats {
  /** Distinct analysis sessions in this browser. */
  analyses: number
  /** ISO date of the first one, so the count can be given a timeframe. */
  since: string
  /** Exports completed. A stronger signal than analyses — it means it worked. */
  exports: number
}

const EMPTY: UsageStats = { analyses: 0, since: '', exports: 0 }

const read = (): UsageStats => {
  if (typeof window === 'undefined') return EMPTY
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw) as Partial<UsageStats>
    return {
      // Coerced rather than trusted: this is user-writable storage, and a
      // hand-edited value must not be able to render NaN into the page.
      analyses: Number.isFinite(parsed.analyses) ? Math.max(0, Number(parsed.analyses)) : 0,
      exports: Number.isFinite(parsed.exports) ? Math.max(0, Number(parsed.exports)) : 0,
      since: typeof parsed.since === 'string' ? parsed.since : '',
    }
  } catch {
    return EMPTY
  }
}

const write = (stats: UsageStats): void => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(stats))
  } catch {
    /* Private mode or storage full. A counter is not worth an error path. */
  }
}

export const getUsage = (): UsageStats => read()

/**
 * Counts one analysis session, not one keystroke.
 *
 * The engine runs on every character typed; incrementing there would report
 * thousands and be transparently meaningless. One per hour per browser is a
 * defensible definition of "a time you sat down and worked on your resume".
 */
const SESSION_MS = 60 * 60 * 1000

export const recordAnalysis = (now: Date = new Date()): void => {
  const stats = read()
  const last = stats.since ? Date.parse(window.sessionStorage.getItem(`${KEY}:last`) ?? '') : NaN
  const nowMs = now.getTime()
  if (Number.isFinite(last) && nowMs - last < SESSION_MS) return

  try {
    window.sessionStorage.setItem(`${KEY}:last`, now.toISOString())
  } catch {
    /* Ignored — worst case the count is slightly conservative. */
  }

  write({
    analyses: stats.analyses + 1,
    exports: stats.exports,
    since: stats.since || now.toISOString().slice(0, 10),
  })
}

export const recordExport = (): void => {
  const stats = read()
  write({ ...stats, exports: stats.exports + 1 })
}
